import { Trans, t } from "@web-monorepo/i18n/macro";
import {
  DDSBodyText,
  DDSButton,
  DDSDialog,
  DDSDialogBody,
  DDSDialogContent,
  DDSDialogFooter,
  DDSDialogHeader,
  DDSLabelText,
} from "@web-monorepo/dds";
import { getImageSrc } from "@web-monorepo/static-assets";
import React from "react";
import { GroupRestriction } from "src/pods/groups/createGroups";
import { Student } from "src/pods/students";
import SelectStudents from "src/shared/components/SelectStudents";
import styles from "./GroupRestrictions.module.css";

const logo = getImageSrc("toolkit/src/pods/groups/assets/group_creator.png");

type GroupRestrictionsProps = {
  classId: string;
  editGroupRestrictions: ({
    classId,
    groupOfStudentIdGroups,
  }: {
    classId: string;
    groupOfStudentIdGroups: GroupRestriction[];
  }) => void;
  onRequestHide: () => void;
  students: Student[];
  groupRestrictions: GroupRestriction[];
};

type GroupRestrictionsState = {
  askForRestriction: boolean;
  newRestriction: GroupRestriction | null;
};

const initialState = {
  askForRestriction: false,
  newRestriction: null,
};

// eslint-disable-next-line react-prefer-function-component/react-prefer-function-component
export default class GroupRestrictions extends React.Component<GroupRestrictionsProps, GroupRestrictionsState> {
  constructor(props: GroupRestrictionsProps) {
    super(props);
    this.state = initialState;
  }

  render() {
    const { students, groupRestrictions } = this.props;
    const { askForRestriction } = this.state;

    return (
      <>
        <DDSDialog data-name="group_restrictions:dialog" open>
          <DDSDialogContent data-name="group_restrictions:dialog:content" onClose={this.props.onRequestHide}>
            <DDSDialogHeader title={t`Don't group together`} onClose={this.props.onRequestHide} />
            <DDSDialogBody>
              <section className={styles.content}>
                <img src={logo} className={styles.logo} alt="" />
                <DDSBodyText as="p" className={styles.description}>
                  <Trans>Create sets of students who you don't want grouped together.</Trans>
                </DDSBodyText>
                <DDSButton
                  variant="unstyled"
                  data-name="group_restrictions:render:group_maker_restrictions_choose_students"
                  className={`${styles.cell} ${styles.button}`}
                  onClick={this.openAddRestriction}
                >
                  <DDSLabelText variant="accent">
                    <Trans>Choose students</Trans>
                  </DDSLabelText>
                </DDSButton>
                <div>{groupRestrictions && groupRestrictions.map(this._renderRestriction)}</div>
              </section>
            </DDSDialogBody>
          </DDSDialogContent>
        </DDSDialog>
        {askForRestriction && (
          <DDSDialog data-name="group_restrictions:select_students_dialog" open>
            <DDSDialogContent
              data-name="group_restrictions:select_students_dialog:content"
              onClose={this.cancelAddRestriction}
            >
              <DDSDialogHeader title={t`Choose students`} onClose={this.cancelAddRestriction} />
              <DDSDialogBody>
                <SelectStudents students={students} onChange={this.updateNewRestriction} />
              </DDSDialogBody>
              <DDSDialogFooter>
                <DDSButton data-name="select_students:done" onClick={this.confirmAddRestriction}>
                  <Trans>Done</Trans>
                </DDSButton>
              </DDSDialogFooter>
            </DDSDialogContent>
          </DDSDialog>
        )}
      </>
    );
  }

  _renderStudent = (studentId: string) => {
    const student = this.props.students.find((student) => student._id === studentId);
    if (!student) return null;
    const lastLetter = student.lastName.slice(0, 1);

    return (
      <div key={student._id} className={styles.student}>
        <img className={styles.avatar} src={student.avatar} alt="" />
        <DDSBodyText level="md" className={styles.name}>
          {student.firstName} {lastLetter}.
        </DDSBodyText>
      </div>
    );
  };

  _renderRestriction = (studentIds: GroupRestriction, index: number) => {
    const restrictions = studentIds.map(this._renderStudent).filter(Boolean);
    if (restrictions.length > 0) {
      return (
        <section key={index} className={`${styles.cell} ${styles.restriction}`}>
          <DDSButton
            variant="unstyled"
            data-name="group_restrictions:clear_student_selection"
            className={styles.deleteButton}
            onClick={() => this.deleteRestriction(studentIds)}
            aria-label={t`Clear student selection`}
          >
            ×
          </DDSButton>
          {restrictions}
        </section>
      );
    }
    return null;
  };

  openAddRestriction = () => {
    this.setState({ askForRestriction: true, newRestriction: [] });
  };

  cancelAddRestriction = () => {
    this.setState({ askForRestriction: false, newRestriction: null });
  };

  confirmAddRestriction = () => {
    const { classId, groupRestrictions } = this.props;
    const { newRestriction } = this.state;
    if (newRestriction?.length) {
      this.props.editGroupRestrictions({
        classId,
        groupOfStudentIdGroups: [...(groupRestrictions || []), newRestriction],
      });
    }
    this.setState({ askForRestriction: false, newRestriction: null });
  };

  updateNewRestriction = (selectedStudents: Student[]) => {
    this.setState({ newRestriction: selectedStudents.map((student) => student._id) });
  };

  deleteRestriction = (restriction: GroupRestriction) => {
    const { classId, groupRestrictions } = this.props;
    this.props.editGroupRestrictions({
      classId,
      groupOfStudentIdGroups: groupRestrictions.filter((r) => r !== restriction),
    });
  };
}
